// AGC: CustomListSettingsPage - Generic settings page for managing a list of items
// AGC: with built-in (read-only) defaults + user-added custom entries.
// AGC: Used by Task types, Machinery types, Crop types, etc. Same pattern as CategoriasSettings
// AGC: but reusable across multiple Sistema sub-pages.

import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Trash2, Pencil, X, Tag } from 'lucide-react'
import { TopBar } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { useFarms } from '../../context/FarmContext'
import { useAuth } from '../../hooks/useAuth'
import { persistCustomList } from '../../hooks/useCustomList'
import type { CustomListItem } from '../inventory/ManageCategoriesUnitsModal'
import { slugFromLabel } from '../inventory/ManageCategoriesUnitsModal'

interface CustomListSettingsPageProps {
  title: string
  defaultSectionTitle: string
  customSectionTitle: string
  emptyText: string
  addPlaceholder: string
  /** AGC: Built-in read-only items (sourced from app constants, locale-aware) */
  builtInItems: CustomListItem[]
  /** AGC: localStorage key prefix (actual key = `${storageKeyPrefix}_${farmId}`) */
  storageKeyPrefix: string
  onBack: () => void
  /** AGC: Optional icon override for list rows (defaults to Tag) */
  Icon?: React.ComponentType<{ className?: string }>
}

/**
 * AGC: Generic page that shows read-only built-in items + editable custom items.
 * AGC: Custom items persist in localStorage per farm. Same pattern as CategoriasSettings.
 */
export const CustomListSettingsPage: React.FC<CustomListSettingsPageProps> = ({
  title,
  defaultSectionTitle,
  customSectionTitle,
  emptyText,
  addPlaceholder,
  builtInItems,
  storageKeyPrefix,
  onBack,
  Icon = Tag,
}) => {
  const { t } = useTranslation()
  const { currentFarm } = useFarms()
  const { user } = useAuth()
  const farmIdForStorage = currentFarm?.id || 'default'

  const [customItems, setCustomItems] = useState<CustomListItem[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [newLabel, setNewLabel] = useState('')

  // AGC: Load custom items from localStorage on farm change
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${storageKeyPrefix}_${farmIdForStorage}`)
      if (raw) {
        const parsed = JSON.parse(raw) as CustomListItem[]
        if (Array.isArray(parsed)) setCustomItems(parsed)
        else setCustomItems([])
      } else {
        setCustomItems([])
      }
    } catch {
      setCustomItems([])
    }
  }, [storageKeyPrefix, farmIdForStorage])

  const persist = useCallback(
    (list: CustomListItem[]) => {
      setCustomItems(list)
      persistCustomList(storageKeyPrefix, farmIdForStorage, list, user?.uid)
    },
    [storageKeyPrefix, farmIdForStorage, user?.uid]
  )

  const makeUniqueId = (label: string): string => {
    const base = slugFromLabel(label)
    const allIds = new Set([
      ...builtInItems.map((i) => i.id),
      ...customItems.map((i) => i.id),
    ])
    let candidate = base
    let n = 0
    while (allIds.has(candidate)) {
      n += 1
      candidate = `${base}_${n}`
    }
    return candidate
  }

  const handleSaveAdd = () => {
    const label = newLabel.trim()
    if (!label) return
    persist([...customItems, { id: makeUniqueId(label), label }])
    setIsAdding(false)
    setNewLabel('')
  }

  const handleSaveEdit = () => {
    if (!editingId) return
    const label = editLabel.trim()
    if (!label) return
    persist(customItems.map((c) => (c.id === editingId ? { ...c, label } : c)))
    setEditingId(null)
    setEditLabel('')
  }

  const handleRemove = (id: string) => {
    persist(customItems.filter((c) => c.id !== id))
  }

  return (
    <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900">
      <TopBar title={title} onBack={onBack} className="bg-white dark:bg-slate-800 shadow-sm" />

      <div className="px-4 py-6 pb-24 space-y-6">
        {/* AGC: Built-in (default) items - read-only */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-1">
            {defaultSectionTitle}
          </h3>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
            {builtInItems.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{item.label}</span>
                <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">
                  {t('inventory.manageCategoriesUnits.builtIn')}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* AGC: Custom items - editable / removable */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-1">
            {customSectionTitle}
          </h3>

          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden">
            {customItems.length === 0 && !isAdding && (
              <p className="px-4 py-4 text-sm text-gray-400 dark:text-slate-500 text-center">
                {emptyText}
              </p>
            )}

            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {customItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>

                  {editingId === item.id ? (
                    <>
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="flex-1 px-2 py-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit()
                          if (e.key === 'Escape') { setEditingId(null); setEditLabel('') }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        disabled={!editLabel.trim()}
                        className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium disabled:opacity-50"
                      >
                        {t('common.save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingId(null); setEditLabel('') }}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{item.label}</span>
                      <button
                        type="button"
                        onClick={() => { setEditingId(item.id); setEditLabel(item.label) }}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                        aria-label={t('common.edit')}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(item.id)}
                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        aria-label={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}

              {/* AGC: Inline add-new row */}
              {isAdding && (
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-slate-700/40">
                  <input
                    type="text"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder={addPlaceholder}
                    className="flex-1 px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveAdd()
                      if (e.key === 'Escape') { setIsAdding(false); setNewLabel('') }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSaveAdd}
                    disabled={!newLabel.trim()}
                    className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
                  >
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setIsAdding(false); setNewLabel('') }}
                    className="px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 text-sm"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {!isAdding && (
            <button
              type="button"
              onClick={() => { setIsAdding(true); setNewLabel('') }}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border border-dashed border-emerald-400 dark:border-emerald-600 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('inventory.manageCategoriesUnits.addOne')}
            </button>
          )}
        </section>
      </div>
    </div>
  )
}
